import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import FractionInput from "../../../../components/inputs/FractionInput";
import {
  samplePolynomialCurve,
} from "./helpers/smoothGraph";

/* =============================================================
   ReadZeroFromGraphTask
   - wykres jak w MaturaPiecewiseFunctionOpenTask: segmenty AL-B i B-CR
   - łamana CIĄGŁA (2 odcinki, jedno załamanie)
   - ZAWSZE jedno miejsce zerowe i ZAWSZE całkowite
============================================================= */

const TOL = 1e-6;

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function eq(a, b) {
  return Math.abs(a - b) < TOL;
}

function pickClosedFlag() {
  return randInt(0, 1) === 1;
}

function isInt(x) {
  return Math.abs(x - Math.round(x)) < 1e-9;
}

function formatFracOrIntLatex(n) {
  // tu n jest całkowite (gwarantujemy), ale zostawiamy spójnie
  return `${n}`;
}

/**
 * Generator w stylu "V" z MaturaPiecewiseFunctionUtils.tryGenerateV(),
 * tylko uproszczony i z warunkiem: zeroX całkowite.
 *
 * Konstrukcja:
 *  - wybieramy h (załamanie), v < 0 (wierzchołek pod OX)
 *  - lewy odcinek ma nachylenie ujemne, prawy dodatnie
 *  - dokładnie jedno miejsce zerowe (na jednym ramieniu)
 *  - zeroX = h - v/m (m=1 lub 2) => wymuszamy całkowitość przez v% m == 0
 */
function buildDataContinuousIntegerZero() {
  for (let attempt = 0; attempt < 800; attempt++) {
    const xL = Math.random() < 0.5 ? -5 : -4;
    const xR = Math.random() < 0.5 ? 4 : 5;

    const hCandidates = [-3, -2, -1, 0, 1, 2, 3].filter(
      (x) => x > xL + 1 && x < xR - 1
    );
    if (!hCandidates.length) continue;
    const h = hCandidates[randInt(0, hCandidates.length - 1)];

    // nachylenia (jak w maturze): 1 lub 2
    const mRight = randInt(1, 2);
    const mLeftAbs = randInt(1, 2);

    // wierzchołek poniżej osi OX
    let v = -randInt(1, 4);

    // zdecyduj, na którym ramieniu ma być miejsce zerowe
    // 0 => na prawym (a2>0), 1 => na lewym (a1<0)
    const zeroSide = randInt(0, 1);

    // wymuszamy całkowitość: zeroX = h - v/m
    const mForZero = zeroSide === 0 ? mRight : mLeftAbs;
    if (mForZero === 2 && Math.abs(v) % 2 !== 0) {
      // jak m=2, v musi być parzyste
      v = -2; // stabilny fallback (dalej v<0)
    }

    const m1 = -mLeftAbs;
    const m2 = mRight;

    // funkcje ramion
    const f1 = (x) => m1 * (x - h) + v;
    const f2 = (x) => m2 * (x - h) + v;

    const yL = f1(xL);
    const yR = f2(xR);

    // trzymamy się okna [-6,6]x[-6,6] jak w reszcie
    const maxAbs = Math.max(Math.abs(yL), Math.abs(v), Math.abs(yR));
    if (maxAbs > 5) continue;

    // współczynniki ax+b
    const a1 = m1;
    const b1 = v - m1 * h;
    const a2 = m2;
    const b2 = v - m2 * h;

    // kandydat na zero:
    // dla prawego: 0 = m2(x-h)+v => x = h - v/m2
    // dla lewego:  0 = m1(x-h)+v, ale m1 = -mLeftAbs => x = h + v/mLeftAbs = h - (-v)/mLeftAbs
    const zeroX =
      zeroSide === 0 ? h - v / m2 : h + v / mLeftAbs;

    if (!isInt(zeroX)) continue;

    // zero musi leżeć w (xL, xR) i nie na samych końcach
    if (!(zeroX > xL + 1e-9 && zeroX < xR - 1e-9)) continue;

    // i żeby było dokładnie jedno:
    // dla V z v<0 oba ramiona zwykle przecinają oś OX (dwa zera),
    // ale my to eliminujemy przez domenę: jedno ramię niech nie obejmuje swojego zera.
    // Czyli ustawimy przedziały tak, by tylko "zeroSide" miało swoje przecięcie w dziedzinie.

    // losujemy domknięcia na końcach jak w innych zadaniach
    const leftClosed = pickClosedFlag();
    const rightClosed = pickClosedFlag();

    // Domenę dzielimy w h:
    // - lewa część: [xL, h] lub (xL, h] itd.
    // - prawa część: (h, xR] lub (h, xR) itd.
    // Żeby mieć dokładnie 1 zero, robimy tak:
    //   - jeśli zero ma być na prawym ramieniu, to domena lewego ramienia kończy się w h
    //     (tam nie ma jego zera, bo jego zero jest po lewej od h), a prawy obejmuje swoje zero.
    //   - jeśli zero ma być na lewym ramieniu, analogicznie prawy nie obejmuje swojego zera.
    //
    // Dla V z v<0: lewy zero jest < h, prawy zero jest > h.
    // Więc wystarczy: zawsze dzielimy w h i oba ramiona są w domenie,
    // ale wtedy mamy DWA zera. Żeby mieć JEDNO, bierzemy tylko jedno ramię w domenie
    // + dokładamy krótki “ogon” po drugiej stronie h bez przecięcia OX.
    //
    // Prościej i stabilniej: robimy łamaną ciągłą, ale tak, żeby jedno ramię było całe nad/pod OX.
    // Dla v<0 oba ramiona startują w (h,v) poniżej OX i rosną/maleją,
    // więc każde ramię kiedyś przetnie OX, jeśli domena jest dostatecznie długa.
    // Wymusimy więc, aby na ramieniu “bez zera” odcinek był krótki i cały poniżej OX:
    //  - dobieramy xL/xR tak, żeby “nie-zero” strona nie zdążyła dojść do 0.

    // sprawdzamy “nie-zero” stronę:
    const leftZero = h + v / mLeftAbs;   // < h
    const rightZero = h - v / mRight;    // > h

    // chcemy, by w dziedzinie znalazł się tylko jeden z nich
    // wybór: zeroSide 0 => prawy; wtedy lewy odcinek musi kończyć się zanim dojdzie do lewego zera
    // ALE lewy zero jest zawsze < h, a lewy odcinek idzie od xL do h.
    // Jeśli xL > leftZero, to lewy zero wypadnie poza dziedziną => zostaje tylko prawy.
    // Analogicznie, jeśli xR < rightZero, to prawy zero wypadnie => zostaje tylko lewy.

    let finalXL = xL;
    let finalXR = xR;

    if (zeroSide === 0) {
      // chcemy tylko prawy zero => ustawiamy xL tak, by xL > leftZero
      const needed = Math.ceil(leftZero + 0.001);
      if (needed >= h) continue;
      finalXL = Math.max(finalXL, needed);
      if (finalXL >= h - 1) continue; // musi zostać miejsce na lewy odcinek
    } else {
      // chcemy tylko lewy zero => ustawiamy xR tak, by xR < rightZero
      const needed = Math.floor(rightZero - 0.001);
      if (needed <= h) continue;
      finalXR = Math.min(finalXR, needed);
      if (finalXR <= h + 1) continue;
    }

    // po korektach policzmy wartości na końcach
    const yLeftEnd = f1(finalXL);
    const yRightEnd = f2(finalXR);

    // upewnijmy się, że “nie-zero” strona faktycznie nie przecina OX:
    //   - jeśli zeroSide=0 (prawy), to lewy odcinek ma być cały <0 (bo v<0 i idziemy do h)
    //     wystarczy, by w finalXL było już "po" lewym zerze: wtedy na [finalXL,h] f1<0
    //   - analogicznie dla prawej
    if (zeroSide === 0) {
      if (!(finalXL > leftZero + 1e-9)) continue;
    } else {
      if (!(finalXR < rightZero - 1e-9)) continue;
    }

    // końce w oknie
    const maxAbs2 = Math.max(Math.abs(yLeftEnd), Math.abs(v), Math.abs(yRightEnd));
    if (maxAbs2 > 5) continue;

    const eps = 0;

    const points = {
      A: {
        x: finalXL,
        y: yLeftEnd,
        markerSymbol: leftClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      AL: {
        x: finalXL + eps,
        y: f1(finalXL + eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      B: {
        x: h,
        y: v,
        showDot: false,
        showMarker: false,
        showLabel: false,
      },
      CR: {
        x: finalXR - eps,
        y: f2(finalXR - eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      C: {
        x: finalXR,
        y: yRightEnd,
        markerSymbol: rightClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
    };

    return {
      points,
      segments: [
        { p1: "AL", p2: "B" },
        { p1: "B", p2: "CR" },
      ],
      xRange: [-6, 6],
      yRange: [-6, 6],
      zeroX: Math.round(zeroX),
    };
  }

  // awaryjnie
  return buildDataContinuousIntegerZero();
}

function buildSmoothIntegerZeroData() {
  for (let attempt = 0; attempt < 1000; attempt += 1) {
    const zeroX = randInt(-3, 3);
    const xL = randInt(-5, zeroX - 2);
    const xR = randInt(zeroX + 2, 5);
    const leftClosed = pickClosedFlag();
    const rightClosed = pickClosedFlag();
    const graphKind = Math.random() < 0.5 ? "linear" : "quadratic";
    const orientation = Math.random() < 0.5 ? -1 : 1;

    let fn;
    if (graphKind === "linear") {
      const slope = randInt(1, 2) * orientation;
      fn = (x) => slope * (x - zeroX);
    } else {
      const outsideRoot =
        Math.random() < 0.5 ? xL - randInt(2, 4) : xR + randInt(2, 4);
      const raw = (x) => orientation * (x - zeroX) * (x - outsideRoot);
      const maxAbs = Math.max(Math.abs(raw(xL)), Math.abs(raw(xR)), 1);
      const scale = 4 / maxAbs;
      fn = (x) => raw(x) * scale;
    }

    const yL = fn(xL);
    const yR = fn(xR);
    if (Math.max(Math.abs(yL), Math.abs(yR)) > 5) continue;
    if (Math.min(Math.abs(yL), Math.abs(yR)) < 1) continue;

    return {
      graphKind,
      points: {
        A: {
          x: xL,
          y: yL,
          markerSymbol: leftClosed ? "circle-filled" : "circle-open",
          showLabel: false,
        },
        C: {
          x: xR,
          y: yR,
          markerSymbol: rightClosed ? "circle-filled" : "circle-open",
          showLabel: false,
        },
      },
      curves: [samplePolynomialCurve(fn, xL, xR, [zeroX])],
      xRange: [-6, 6],
      yRange: [-6, 6],
      zeroX,
    };
  }
  return buildSmoothIntegerZeroData();
}

/* -------------------- API taska -------------------- */

export function generateTask() {
  return {
    type: "ReadZeroFromGraphTask",
    data: buildSmoothIntegerZeroData(),
  };
}

export function renderTask({ data }) {
  const sizePx = 330;

  return (
    <div className="flex flex-col gap-3">
      <p>Na wykresie przedstawiono funkcję. Odczytaj jej miejsce zerowe.</p>

      <div style={{ margin: "0 auto", maxWidth: sizePx }}>
        <GraphViewer
          width={sizePx}
          points={data.points}
          curves={data.curves}
          xRange={data.xRange}
          yRange={data.yRange}
        />
      </div>

      <div>
        Miejsce zerowe zapisz w postaci{" "}
        <InlineMath math={"x_0=\\_\\_\\_\\_"} />.
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <strong>Miejsce zerowe:</strong>
      </div>
      <div><InlineMath math={"x_0="} /></div>

      <div className="max-w-[260px]">
        <FractionInput
          value={userAnswer || ""}
          onChange={onChange}
          disabled={disabled}
          placeholder="np. 1"
          label="x_0"
        />
      </div>

      {/* Podgląd: tylko jak user faktycznie coś wpisał i jest raw */}
      {userAnswer && typeof userAnswer === "object" && typeof userAnswer.raw === "string" && userAnswer.raw.trim() ? (
        <div>
          <InlineMath math={`x_0 = ${userAnswer.raw}`} />
        </div>
      ) : null}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  if (!Number.isFinite(parsed)) return false;
  return eq(parsed, data.zeroX);
}

export function renderAnswer({ data }) {
  const latex = formatFracOrIntLatex(data.zeroX);
  return <InlineMath math={`x_0 = ${latex}`} />;
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Miejsce zerowe to taka liczba{" "}
        <InlineMath math={'x_0'} />, dla której <InlineMath math={'f(x_0)=0'} />.
      </div>

      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong> Na wykresie szukamy punktu przecięcia z osią{" "}
        <InlineMath math={'OX'} /> (czyli tam, gdzie <InlineMath math={'y=0'} />).
      </div>

      <div style={{ marginTop: 8 }}>
        <strong>Krok 3.</strong> Odczytujemy z osi <InlineMath math={'x'} /> współrzędną tego punktu:
      </div>

      <div style={{ marginTop: 10 }}>
        <BlockMath math={`x_0 = ${data.zeroX}`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw || !String(userAnswer.raw).trim()) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`x_0 = ${userAnswer.raw}`} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
